import { Permission, PermissionAction, PermissionAsset } from '@redux/types/types';

/**
 * A singleton class that manages user permissions throughout the application.
 * This provides a centralized way to check authorization for assets.
 */
class AuthorizationManager {
  private permissions = new Map<PermissionAsset, PermissionAction>();

  /**
   * Sets the user's permissions. This will overwrite any existing permissions.
   *
   * @param permissions - An array of permission objects.
   */
  public setPermissions(permissions: Permission[]): void {
    this.permissions.clear();
    if (!permissions) return;

    for (const { asset, action } of permissions) {
      this.permissions.set(asset, action);
    }
  }

  /**
   * Checks if a user's permission level is sufficient for a required action.
   * WRITE access is considered sufficient for a READ requirement.
   *
   * @param userAction - The permission action the user has.
   * @param requiredAction - The permission action that is required.
   * @returns True if the user's action is sufficient, false otherwise.
   */
  private isSufficient(
    userAction: PermissionAction | undefined,
    requiredAction: PermissionAction,
  ): boolean {
    if (userAction === undefined) return false;

    return (
      userAction === requiredAction ||
      (userAction === PermissionAction.WRITE && requiredAction === PermissionAction.READ)
    );
  }

  /**
   * Checks if the user has a specific permission for a given asset.
   * It considers wildcard permissions (asset: '*')
   *
   * @param requiredPermissions - A record where keys are assets and values are the required actions.
   * @returns True if the user has all permissions, false otherwise.
   */
  public has(requiredPermissions: Partial<Record<PermissionAsset, PermissionAction>>): boolean {
    const wildcardAction = this.permissions.get(PermissionAsset.ALL);

    return (Object.keys(requiredPermissions) as PermissionAsset[]).every((asset) => {
      const requiredAction = requiredPermissions[asset];
      if (!requiredAction) return true;

      const userAction = this.permissions.get(asset);

      return (
        this.isSufficient(userAction, requiredAction) ||
        this.isSufficient(wildcardAction, requiredAction)
      );
    });
  }

  /**
   * Clears all stored permissions.
   */
  public clear(): void {
    this.permissions.clear();
  }

  /**
   * Gets all stored permissions as an array.
   */
  public getPermissions(): Permission[] {
    const permissions: Permission[] = [];
    this.permissions.forEach((action, asset) => {
      permissions.push({ asset, action });
    });
    return permissions;
  }
}

const authorizationManager = new AuthorizationManager();

export default authorizationManager;
